import {
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  Filter,
  History,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Dropdown } from './dropdown';
import { CONTROLE_BARRA } from './ui';

export interface PresetPeriodo {
  dias: number;
  rotulo: string;
  descricao: string;
  icone: LucideIcon;
}

/** O período escolhido: janela móvel OU intervalo fechado. */
export interface Periodo {
  dias: number;
  inicio?: string;
  fim?: string;
}

/** `Date` para `AAAA-MM-DD`, no fuso local — que é o fuso da fábrica. */
export function emData(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Dias desde o primeiro dia do mês corrente, contando hoje. */
function diasDoMes(): number {
  return new Date().getDate();
}

/** Dias desde 1º de janeiro, contando hoje. */
function diasDoAno(): number {
  const hoje = new Date();
  const janeiro = new Date(hoje.getFullYear(), 0, 1);
  return Math.floor((hoje.getTime() - janeiro.getTime()) / 86_400_000) + 1;
}

/**
 * Os presets de janela MÓVEL — todos terminam hoje.
 *
 * O que não termina hoje ("mês passado") não cabe nesta forma, e vive no
 * intervalo personalizado do mesmo menu. Fingir que cabe daria um número
 * silenciosamente errado, que é pior que não ter a opção.
 */
export const PRESETS_PERIODO: readonly PresetPeriodo[] = [
  { dias: 1, rotulo: 'Hoje', descricao: 'Somente hoje', icone: Sun },
  {
    dias: 7,
    rotulo: 'Últimos 7 dias',
    descricao: 'Janela móvel de 7 dias',
    icone: History,
  },
  {
    dias: diasDoMes(),
    rotulo: 'Este mês',
    descricao: 'Do dia 1 até hoje',
    icone: CalendarRange,
  },
  {
    dias: 30,
    rotulo: 'Últimos 30 dias',
    descricao: 'O padrão da fábrica',
    icone: CalendarDays,
  },
  {
    dias: 90,
    rotulo: 'Últimos 90 dias',
    descricao: 'Trimestre móvel',
    icone: CalendarClock,
  },
  {
    dias: diasDoAno(),
    rotulo: 'Este ano',
    descricao: 'Desde janeiro',
    icone: CalendarDays,
  },
];

/**
 * Escolha de período, no molde da Consulta de Pedidos do Isotec: presets
 * rápidos com descrição, mais um intervalo fechado.
 *
 * A diferença que importa não é estética. "Este mês" é o que a pessoa pensa, e
 * traduzir isso para um número de dias na cabeça, todo dia, é conta que
 * ninguém faz — então ninguém usava as opções que não estavam na lista.
 */
export function PeriodoRapido({
  periodo,
  aoEscolher,
  rotuloVazio,
}: {
  periodo: Periodo;
  aoEscolher: (periodo: Periodo) => void;
  /**
   * O que o botão diz quando NÃO há período escolhido (`dias: 0`).
   *
   * Existe porque nem toda tela nasce filtrada: no funil, o padrão é ver o
   * quadro inteiro. Sem isto o botão anunciava "Últimos 0 dias", que não é um
   * período — é o placeholder vazando para a cara do usuário.
   */
  rotuloVazio?: string;
}) {
  const { dias, inicio, fim } = periodo;
  const personalizado = Boolean(inicio && fim);
  const atual = personalizado ? undefined : PRESETS_PERIODO.find((p) => p.dias === dias);

  // Rascunho do intervalo: só vale quando a pessoa confirma. Aplicar a cada
  // tecla dispararia uma consulta por dígito digitado na data.
  const hoje = emData(new Date());
  const [de, setDe] = useState(inicio ?? hoje);
  const [ate, setAte] = useState(fim ?? hoje);
  const invertido = de > ate;

  return (
    <Dropdown
      rotulo="Escolher período"
      alinhamento="inicio"
      classeGatilho={`${CONTROLE_BARRA} px-3`}
      gatilho={
        <>
          <CalendarDays className="text-tinta-500 size-4" aria-hidden />
          {/* O rótulo do preset quando casa; o intervalo quando é
              personalizado. Nunca "escolha um período": o botão sempre diz o
              que está valendo agora. */}
          {atual?.rotulo ??
            (personalizado
              ? `${curta(inicio)} a ${curta(fim)}`
              : dias > 0
                ? `Últimos ${dias} dias`
                : (rotuloVazio ?? 'Escolher período'))}
          <ChevronDown className="text-tinta-500 size-4" aria-hidden />
        </>
      }
      classePainel="w-96 p-1"
    >
      {(fechar) => (
        <>
          {PRESETS_PERIODO.map((preset) => {
            const ativo = !personalizado && preset.dias === dias;
            return (
              <button
                key={preset.rotulo}
                type="button"
                onClick={() => {
                  aoEscolher({ dias: preset.dias });
                  fechar();
                }}
                className={`flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors ${
                  ativo ? 'bg-marca-50' : 'hover:bg-marca-600/[0.07]'
                }`}
              >
                <preset.icone
                  className={`mt-0.5 size-4 shrink-0 ${ativo ? 'text-marca-600' : 'text-tinta-500'}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm ${ativo ? 'text-marca-700 font-medium' : 'text-tinta-700'}`}
                  >
                    {preset.rotulo}
                  </span>
                  <span className="text-tinta-500 block text-xs">{preset.descricao}</span>
                </span>
                {ativo && (
                  <Check className="text-marca-600 mt-0.5 size-4 shrink-0" aria-hidden />
                )}
              </button>
            );
          })}

          {/*
            Intervalo fechado, no MESMO menu dos presets e não num segundo
            botão: escolher período é uma decisão só. Repartida em dois
            controles, a pessoa fica com um preset marcado e um intervalo
            preenchido, sem saber qual dos dois está valendo.
          */}
          <div className="border-tinta-100 mt-1 border-t px-2 pt-2 pb-1">
            <p
              className={`pb-1.5 text-xs font-semibold tracking-wide uppercase ${
                personalizado ? 'text-marca-700' : 'text-tinta-500'
              }`}
            >
              Período personalizado
            </p>
            {/*
              As datas em cima, o botão numa linha própria logo abaixo.

              Os três lado a lado espremiam cada campo em ~90px: `type="date"`
              renderiza dd/mm/aaaa MAIS o ícone de calendário do navegador, e
              abaixo de ~130px o Chrome corta o ano. Com o botão embaixo, cada
              campo fica com metade do painel e a data cabe inteira.

              O botão continua DENTRO deste bloco (mesma borda, mesmo respiro)
              para não parecer que vale para o menu inteiro: ele aplica o
              intervalo logo acima dele, não os presets.
            */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                aria-label="Início do período"
                value={de}
                onChange={(e) => setDe(e.target.value)}
                className="border-tinta-100 bg-campo text-tinta-900 focus:border-marca-500 h-9 min-w-0 flex-1 rounded-md border px-2 text-sm outline-none"
              />
              <span className="text-tinta-500 shrink-0 text-sm">a</span>
              <input
                type="date"
                aria-label="Fim do período"
                value={ate}
                onChange={(e) => setAte(e.target.value)}
                className="border-tinta-100 bg-campo text-tinta-900 focus:border-marca-500 h-9 min-w-0 flex-1 rounded-md border px-2 text-sm outline-none"
              />
            </div>

            <button
              type="button"
              disabled={invertido}
              title={
                invertido ? 'O início vem depois do fim.' : 'Filtrar por este período'
              }
              onClick={() => {
                aoEscolher({ dias, inicio: de, fim: ate });
                fechar();
              }}
              className="bg-marca-600 enabled:hover:bg-marca-500 text-marca-contraste mt-2 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Filter className="size-4" aria-hidden />
              Filtrar por este período
            </button>
            {/*
              O erro sai por escrito embaixo, e não só apagando o botão: botão
              cinza sem explicação faz a pessoa clicar de novo achando que a
              tela travou.
            */}
            {invertido && (
              <p className="text-erro-600 pt-1 text-xs">O início vem depois do fim.</p>
            )}
          </div>
        </>
      )}
    </Dropdown>
  );
}

/** `2026-08-23` → `23/08`. Sem o ano: o rótulo é um botão, não um relatório. */
function curta(iso: string | undefined): string {
  if (!iso) return '';
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}
